import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import '../Styles/header.css';

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'about' | 'platforms' | 'events' | 'resources' | null
  const headerRef = useRef(null);
  const hoverCloseTimer = useRef(null);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  // Desktop hover support: open immediately on enter, close on leave with
  // a short delay so moving the mouse from the toggle button down into the
  // dropdown menu (across the small gap) doesn't accidentally close it.
  // Only wired up for pointer devices that support hover (desktop/laptop);
  // touch/mobile continues to rely on the existing tap-to-toggle behavior.
  const openDropdownOnHover = (name) => {
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
    setOpenDropdown(name);
  };

  const closeDropdownOnHoverLeave = () => {
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
    hoverCloseTimer.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  // Close the open dropdown whenever the user clicks/taps anywhere
  // outside the header (blank page area, another section, etc.),
  // or presses Escape.
  useEffect(() => {
    if (!openDropdown) return;

    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenDropdown(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openDropdown]);

  // Clean up any pending hover-close timer on unmount.
  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    };
  }, []);

  return (
    <header className="header" ref={headerRef}>
      <div className="header-logo">
        <Link to="/" onClick={closeMenu}>
          <img
            src="/assets/vnkm_logo.png"
            alt=""
            className="logo-img"
          />
        </Link>
      </div>

      <div className="header-right">
        <nav className={`header-nav ${menuOpen ? 'active' : ''}`}>
          <Link to="/home" className="nav-link" onClick={closeMenu}>Home</Link>

          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openDropdownOnHover('about')}
            onMouseLeave={closeDropdownOnHoverLeave}
          >
            <button
              type="button"
              className="nav-link dropdown-toggle"
              onClick={() => toggleDropdown('about')}
              aria-expanded={openDropdown === 'about'}
            >
              About us
              <img
              src="/assets/Header_arrow_icon.png"
              alt="Dropdown Arrow"
              className={`dropdown-chevron ${openDropdown === 'about' ? 'open' : ''}`}
            />
            </button>

            {openDropdown === 'about' && (
              <div className="dropdown-menu">
                <span className="dropdown-caret"></span>
                <Link to="/whatwedo " className="dropdown-link" onClick={closeMenu}>
                  What We Do
                </Link>
                <Link to="/ourteam" className="dropdown-link" onClick={closeMenu}>
                  Our Team
                </Link>
                <Link to="/ourpartner" className="dropdown-link" onClick={closeMenu}>
                  Partner With Us
                </Link>
              </div>
            )}
          </div>

          

          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openDropdownOnHover('platforms')}
            onMouseLeave={closeDropdownOnHoverLeave}
          >
            <button
              type="button"
              className="nav-link dropdown-toggle"
              onClick={() => toggleDropdown('platforms')}
              aria-expanded={openDropdown === 'platforms'}
            >
              Our Platforms
              <img
              src="/assets/Header_arrow_icon.png"
              alt="Dropdown Arrow"
              className={`dropdown-chevron ${openDropdown === 'platforms' ? 'open' : ''}`}
            />
            </button>

            {openDropdown === 'platforms' && (
              <div className="dropdown-menu">
                <span className="dropdown-caret"></span>
                <Link to="/hrcommunity" className="dropdown-link" onClick={closeMenu}>
                  HR Community Network
                </Link>
                <Link to="/industryacademia" className="dropdown-link" onClick={closeMenu}>
                  Industry-Academia Connect
                </Link>
                <Link to="/employeebranding" className="dropdown-link" onClick={closeMenu}>
                  Employer Branding & Recognition
                </Link>
              </div>
            )}
          </div>
          <Link to="/getcertified" className="nav-link" onClick={closeMenu}>Get Certified</Link>

          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openDropdownOnHover('events')}
            onMouseLeave={closeDropdownOnHoverLeave}
          >
            <button
              type="button"
              className="nav-link dropdown-toggle"
              onClick={() => toggleDropdown('events')}
              aria-expanded={openDropdown === 'events'}
            >
              Events
              <img
              src="/assets/Header_arrow_icon.png"
              alt="Dropdown Arrow"
              className={`dropdown-chevron ${openDropdown === 'events' ? 'open' : ''}`}
            />
            </button>

            {openDropdown === 'events' && (
              <div className="dropdown-menu">
                <span className="dropdown-caret"></span>
                <Link to="/events" className="dropdown-link" onClick={closeMenu}>
                  Upcoming Events
                </Link>
               <Link to="/gallery" className="dropdown-link" onClick={closeMenu}>
                Event Gallery
              </Link>
              </div>
            )}
          </div>

          <div
            className="nav-item-dropdown"
            onMouseEnter={() => openDropdownOnHover('resources')}
            onMouseLeave={closeDropdownOnHoverLeave}
          >
            <button
              type="button"
              className="nav-link dropdown-toggle"
              onClick={() => toggleDropdown('resources')}
              aria-expanded={openDropdown === 'resources'}
            >
              Resources
              <img
              src="/assets/Header_arrow_icon.png"
              alt="Dropdown Arrow"
              className={`dropdown-chevron ${openDropdown === 'resources' ? 'open' : ''}`}
            />
            </button>

            {openDropdown === 'resources' && (
              <div className="dropdown-menu">
                <span className="dropdown-caret"></span>
                <Link to="/ourclub" className="dropdown-link" onClick={closeMenu}>
                  Online HR Learning & Certifications
                </Link>
                <Link to="/ourclub" className="dropdown-link" onClick={closeMenu}>
                  Our Clubs
                </Link>
              </div>
            )}
          </div>

          <div className="header-cta mobile-cta">
            <Link to="/jointhecommunity" className="join-btn" onClick={closeMenu}>
              Join the Community
              <img
                src="/assets/home_arrow_icon.png"
                alt="Arrow Icon"
                className="arrow-icon"
              />
            </Link>
          </div>
        </nav>

        <div className="header-cta desktop-cta">
          <Link to="/jointhecommunity" className="join-btn">
            Join the Community
              <img
                src="/assets/home_arrow_icon.png"
                alt="Arrow Icon"
                className="arrow-icon"
              />
          </Link>
        </div>
      </div>

      <button
        className={`burger-btn ${menuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={menuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {menuOpen && <div className="nav-overlay" onClick={closeMenu}></div>}
    </header>
  );
}

export default Header;